import { DedupeComponent } from '../../dedupe/dedupe.component';
import { RollOffBoardComponent } from './rolloff-board.component';
import { RollOffListComponent } from './rolloff-list.component';
import { Component, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AuthService } from '../../../services/auth.service';
import { OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import {
  RollOffHireDialogComponent, RollOffHireDialogData,
} from './rolloff-hire-dialog.component';
import { RollOffOpportunity } from './rolloff-opportunity.model';
import { RollOffContext, RollOffContextService } from './rolloff-context.service';
import { ROLLOFF_VIEWS } from './rolloff-views';

/**
 * Keeps the hire collection mounted while a routed hire opens in its place.
 *
 * WHICH COLLECTION is now the URL's business - /rolloff-hires/table and
 * /rolloff-hires/board are two routes over this one workspace, the way Leads
 * has always worked. The board answers "what is where in the pipeline"; the
 * table answers everything a card has no room for: what was quoted, which
 * hires end this week, sorted by price.
 *
 * The detail is shared by both. A hire opened from a row and a hire opened
 * from a card are the same pane, so the two views cannot drift on what a hire
 * looks like.
 */
@Component({
  selector: 'app-rolloff-workspace',
  templateUrl: './rolloff-workspace.component.html',
  host: { class: 'workspace-master-detail-page' },
})
export class RollOffWorkspaceComponent implements OnInit {
  readonly views = ROLLOFF_VIEWS;
  @ViewChild(RollOffBoardComponent) board?: RollOffBoardComponent;
  @ViewChild(RollOffListComponent) list?: RollOffListComponent;
  reviewClosed(component: unknown): void {
    if (component instanceof DedupeComponent && component.needsRefresh) {
      this.board?.load();
      this.list?.onTransferChanged();
    }
  }

  /**
   * The stage vocabulary and roster, held HERE rather than on the board.
   *
   * The detail pane opens over either presentation, so what a hire's stage is
   * printed with cannot belong to one of them - a hire opened from a table row
   * would have shown an id where one opened from a card shows a name.
   */
  context: RollOffContext = { stages: [], transitions: [], reps: [] };

  /**
   * Which presentation this route asked for.
   *
   * Read off THIS route's data, falling back to the board - the address
   * /rolloff-hires/:id predates the two views and carries no view at all, and
   * it has to keep opening the hire rather than an empty screen.
   */
  get view(): 'list' | 'board' {
    return this.route.snapshot.data['rollOffCollectionView'] === 'list'
      ? 'list' : 'board';
  }

  get showBoard(): boolean { return this.view === 'board'; }

  get detailId(): number | null {
    const raw = this.route.firstChild?.snapshot.paramMap.get('id');
    if (raw == null) return null;
    const id = Number(raw);
    return Number.isInteger(id) && id > 0 ? id : null;
  }

  get detailOpen(): boolean { return this.detailId !== null || this.route.firstChild?.snapshot?.data?.['dedupeTable'] != null; }

  constructor(
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly contextService: RollOffContextService,
    public readonly auth: AuthService,
    private readonly dialog: MatDialog,
  ) {}

  ngOnInit(): void {
    this.contextService.context().subscribe(context => { this.context = context; });
  }

  /** Whether this account may move a hire, which both presentations and the
   *  detail all have to agree on. */
  get canManage(): boolean {
    return this.auth.hasPermission('LeadPipeline.Manage');
  }

  openDetail(id: number): void {
    this.router.navigate([String(id)], { relativeTo: this.route });
  }

  /** Back to the collection this hire was opened from, not to a fixed one:
   *  closing a detail must not walk somebody from the Table to the Board. */
  closeDetail(): void {
    this.router.navigate(['.'], { relativeTo: this.route });
  }

  /**
   * Edit the hire the detail pane is showing.
   *
   * Opened HERE rather than delegated to the board, because the board is not
   * mounted when the Table is on screen - a hire opened from a row would have
   * had a disabled Edit for no reason a person could see. The dialog itself is
   * the board's, unchanged; what differs is only who asked for it.
   *
   * The saved hire is not patched into anything from here: the collection
   * below owns its own rows, and it re-reads them. Two owners of one list is
   * how a row and its detail come to disagree.
   */
  editHire(hire: RollOffOpportunity): void {
    const data: RollOffHireDialogData = { hire, reps: this.context.reps };
    this.dialog
      .open(RollOffHireDialogComponent, { width: '560px', data })
      .afterClosed()
      .subscribe((saved?: RollOffOpportunity | null) => {
        if (!saved) return;
        // Re-enter the same address so the collection and the detail both read
        // the hire again, rather than one of them keeping the old copy.
        this.router.navigate(['.'], {
          relativeTo: this.route, queryParamsHandling: 'preserve',
        });
      });
  }
}
